<script setup>
import { useStore } from 'vuex';
import ResumeLabelsSelect from '@/components/ResumeLabelsSelect.vue';
import { computed, onBeforeMount, ref, watch } from 'vue';
import ArrowRightIcon from '@/assets/icons/arrow-right.svg';
import HelpIcon from '@/assets/icons/help.svg';
import FireArea from '@/components/inputs/FireArea.vue';

defineEmits(['back']);

const store = useStore();
const props = defineProps({
  isNewProfile: Boolean,
});

const isOrganization = ref(false);
const resumeName = ref('');
const lastname = ref('');
const firstname = ref('');
const poste = ref('');
const labels = ref([]);

const user = computed(() => store.getters.user);
const popupResolve = computed(() => store.getters.popupResolve);
const popupData = computed(() => store.getters.popupData);

onBeforeMount(() => {
  if (user.value.organization) {
    isOrganization.value = popupData.value.workspaceType === 1;
  }

  const candidate = popupData.value.candidate;
  resumeName.value = candidate.firstname;
  lastname.value = props.isNewProfile ? '' : candidate.lastname;
  firstname.value = props.isNewProfile ? '' : candidate.firstname;
  poste.value = candidate.jobTitle;
  labels.value = popupData.value.labels;
});

watch(isOrganization, newValue => {
  labels.value = labels.value.filter(label => label.fromOrganization === newValue || !label.id);
});

const duplicate = async () => {
  const result = await store.dispatch('resumes.duplicate', {
    id: popupData.value.id,
    jobTitle: poste.value,
    referenceLink: '',
    labels: labels.value || [],
    isOrganization: isOrganization.value,
    firstname: props.isNewProfile ? firstname.value : '',
    lastname: props.isNewProfile ? lastname.value : '',
  });
  popupResolve.value(result);
};
</script>

<template>
  <div class="duplicate-resume-container">
    <div class="container">
      <div class="title-container no-margin">
        <ArrowRightIcon class="arrow" @click="$emit('back')" />
        <div class="title">{{ $t('popUp.duplicateResume.infos') }}</div>
      </div>
    </div>
    <div class="container">
      <div v-if="user.organization && user.organization.name" class="field checkboxs">
        <div class="checkbox-container" @click="isOrganization = true">
          <div class="checkbox" :class="{ checked: isOrganization }"></div>
          <div class="title">{{ user.organization.name }}</div>
        </div>

        <div class="checkbox-container" @click="isOrganization = false">
          <div class="checkbox" :class="{ checked: !isOrganization }"></div>
          <div class="title">{{ $t('popUp.duplicateResume.personalGroup') }}</div>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="field">
        <div class="title">{{ $t('popUp.duplicateResume.firstName') }}*</div>
        <input v-model="firstname" type="text" class="text-input opacity" :disabled="!isNewProfile" />
      </div>

      <div class="field">
        <div class="title">{{ $t('popUp.duplicateResume.name') }}*</div>
        <input v-model="lastname" type="text" class="text-input opacity" :disabled="!isNewProfile" />
      </div>

      <div class="field">
        <div class="title">{{ $t('popUp.duplicateResume.jobTitle') }}*</div>

        <FireArea name="jobTitle" :value="poste" :template-div-container="'job'" @change="value => (poste = value)" />
      </div>

      <div class="field">
        <div class="title-group">
          <div class="title">{{ $t('popUp.duplicateResume.labels') }}</div>

          <div v-if="user.organization && user.organization.name" class="help-icon-container">
            <HelpIcon class="help-icon" />
            <div class="info">{{ $t('popUp.duplicateResume.labelsHelp') }}</div>
          </div>
        </div>
        <ResumeLabelsSelect v-model="labels" :user="user" :is-organization="isOrganization" />
      </div>

      <div class="field infos">
        {{ $t('popUp.duplicateResume.mandatoryField') }}
      </div>
    </div>

    <div class="container center">
      <div class="main-button button-cancel" @click="popupResolve(false)">
        <div class="text">{{ $t('popUp.duplicateResume.cancelButton') }}</div>
      </div>

      <div class="main-button button-create" @click="duplicate">
        <div class="text">{{ $t('popUp.duplicateResume.validateButton') }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/inputs.scss';
@use '@/styles/buttons.scss';

.duplicate-resume-container {
  max-width: 428px;
  padding: 22px 0 0;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  .container {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: column;

    &.center {
      width: 100%;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      padding: 0 30px;
      box-sizing: border-box;
      margin: 0 0 6px 0;

      .main-button {
        margin: 0;
      }

      .button-cancel {
        margin-right: 22px;
      }
    }
  }
  .field {
    width: 100%;
    margin: 6px 0;

    .title-group {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .help-icon-container {
        position: relative;
        width: 20px;
        height: 20px;
        cursor: pointer;

        .help-icon {
          height: 100%;
          width: 100%;
          color: #333333;
          transition: opacity 0.2s;

          &:hover {
            opacity: 0.8;
          }
        }

        &:hover .info {
          scale: 1;
          pointer-events: auto;
        }

        .info {
          position: absolute;
          background-color: #fff;
          box-shadow: 0 1px 4px 0 rgba(75, 75, 75, 0.5);
          border: 2px solid rgba(45, 100, 112, 1);
          border-radius: 8px;
          padding: 9px;
          right: -14px;
          top: 150%;
          text-align: center;
          line-height: 0.8rem;
          width: 10.5rem;
          transform-origin: 160px top;
          scale: 0;
          pointer-events: none;
          transition: all 0.2s ease-in-out;
          user-select: none;
          font-size: 12px;
          z-index: 99;

          &::after {
            content: '';
            position: absolute;
            background-color: white;
            rotate: 45deg;
            top: -7.5px;
            right: 16px;
            width: 10px;
            height: 10px;
            border-left: 2px solid rgba(45, 100, 112, 1);
            border-top: 2px solid rgba(45, 100, 112, 1);
          }
        }
      }
    }

    .title {
      height: 19px;
      color: #939292;
      font-size: 16px;
      margin: 0 0 4px;
    }

    input {
      width: 100%;
    }

    &.checkboxs {
      display: flex;
      align-items: center;
      .checkbox-container {
        display: flex;
        align-items: center;
        margin-right: 24px;
        cursor: pointer;
        .title {
          height: 19px;
          color: #333333;
          font-size: 16px;
          margin-left: 8px;
        }
      }
    }

    &.infos {
      height: 15px;
      width: 100%;
      text-align: right;
      color: colors.$light-blue-color;
      font-size: 12px;
      font-style: italic;
      margin: 0 0 30px;
    }
  }
  > div:first-child:nth-last-child(1) {
    margin-bottom: 12px;
  }

  .title-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5px;

    &.no-margin {
      margin: 0 0 12px 0;
    }
    .arrow {
      rotate: 180deg;
      width: 12px;
      height: 12px;
      cursor: pointer;
    }

    .title {
      color: #000000;
      font-size: 18px;
      font-weight: bold;
    }
    .indicator {
      margin-top: 44px;
      margin-right: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 29px;
      width: 140px;
      border-radius: 3px;
      background-color: #e4e4e4;
      color: #606060;
      font-size: 16px;
    }
  }
  .validate {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
